import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { UserIcon, MailIcon, PhoneIcon, GlobeIcon, CalendarIcon, UsersIcon, SendIcon, CheckIcon, MessageCircleIcon, StarIcon, ShieldCheckIcon, HeartIcon, CompassIcon } from 'lucide-react';
interface PlanTripPageProps {
  onNavigate: (page: string) => void;
}
function PlanTripHero({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <section className="relative w-full min-h-[45vh] flex items-end overflow-hidden" data-id="element-1860">
      <div className="absolute inset-0" data-id="element-1861">
        <img src="https://images.unsplash.com/photo-1486911278844-a81c5267e227?w=1920&q=80" alt="Himalayan mountain trail at sunrise" className="w-full h-full object-cover" data-id="element-1862" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" data-id="element-1863" />
      </div>
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 pt-32" data-id="element-1864">
        <nav className="mb-5" aria-label="Breadcrumb" data-id="element-1865">
          <ol className="flex items-center gap-2 text-sm text-white/60" data-id="element-1866">
            <li data-id="element-1867">
              <button onClick={() => onNavigate('home')} className="hover:text-white transition-colors" data-id="element-1868">
                Home
              </button>
            </li>
            <li data-id="element-1869">/</li>
            <li className="text-[#D4A853]" data-id="element-1870">Plan a Trip</li>
          </ol>
        </nav>
        <motion.h1 initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4" data-id="element-1871">
          Plan Your{' '}
          <span className="italic font-medium text-[#D4A853]" data-id="element-1872">Dream Trip</span>
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.15
      }} className="text-white/70 text-lg max-w-2xl font-light" data-id="element-1873">
          Share your travel preferences and our experts will craft a bespoke
          Nepal itinerary just for you.
        </motion.p>
      </div>
    </section>;
}
function TripForm() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const [submitted, setSubmitted] = useState(false);
  const [tripTypes, setTripTypes] = useState<string[]>([]);
  const toggleTripType = (type: string) => {
    setTripTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };
  const tripTypeOptions = ['Luxury Trek', 'Cultural Tour', 'Wildlife Safari', 'Helicopter Tour', 'City Tour'];
  return <section className="w-full bg-white py-20 sm:py-28" ref={ref} data-id="element-1874">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1875">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16" data-id="element-1876">
          {/* Form */}
          <div className="lg:col-span-3" data-id="element-1877">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.7
          }} data-id="element-1878">
              <div className="flex items-center gap-3 mb-6" data-id="element-1879">
                <span className="text-[#D4A853]/40 text-xs" data-id="element-1880">◆</span>
                <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-1881" />
                <span className="text-[#D4A853]/40 text-xs" data-id="element-1882">◆</span>
              </div>
              <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-id="element-1883">
                Tell Us About Your Trip
              </h2>
              <p className="text-slate-500 font-light text-lg mb-8" data-id="element-1884">
                Fill in the details below and our travel experts will design a
                personalized itinerary within 24 hours.
              </p>
            </motion.div>

            {submitted ? <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="text-center py-16 bg-[#FAF8F5] rounded-2xl border border-[#D4A853]/10" data-id="element-1885">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 200,
              delay: 0.1
            }} className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1886">
                  <CheckIcon className="w-8 h-8 text-teal-600" data-id="element-1887" />
                </motion.div>
                <h3 className="font-playfair text-2xl font-bold text-slate-900 mb-2" data-id="element-1888">
                  Trip Request Submitted!
                </h3>
                <p className="text-slate-500 text-sm max-w-sm mx-auto" data-id="element-1889">
                  Our travel experts are already working on your personalized
                  itinerary. We'll be in touch within 24 hours.
                </p>
              </motion.div> : <form onSubmit={e => {
            e.preventDefault();
            setSubmitted(true);
            setTimeout(() => setSubmitted(false), 5000);
          }} className="space-y-5" data-id="element-1890">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-1891">
                  <div data-id="element-1892">
                    <label htmlFor="pt-name" className="block text-sm text-slate-700 font-medium mb-1.5" data-id="element-1893">
                      Full Name
                    </label>
                    <div className="relative" data-id="element-1894">
                      <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1895" />
                      <input id="pt-name" type="text" required placeholder="Your full name" className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors" data-id="element-1896" />
                    </div>
                  </div>
                  <div data-id="element-1897">
                    <label htmlFor="pt-email" className="block text-sm text-slate-700 font-medium mb-1.5" data-id="element-1898">
                      Email Address
                    </label>
                    <div className="relative" data-id="element-1899">
                      <MailIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1900" />
                      <input id="pt-email" type="email" required placeholder="you@example.com" className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors" data-id="element-1901" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-1902">
                  <div data-id="element-1903">
                    <label htmlFor="pt-phone" className="block text-sm text-slate-700 font-medium mb-1.5" data-id="element-1904">
                      Phone Number
                    </label>
                    <div className="relative" data-id="element-1905">
                      <PhoneIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1906" />
                      <input id="pt-phone" type="tel" placeholder="+1 234 567 8900" className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors" data-id="element-1907" />
                    </div>
                  </div>
                  <div data-id="element-1908">
                    <label htmlFor="pt-country" className="block text-sm text-slate-700 font-medium mb-1.5" data-id="element-1909">
                      Country
                    </label>
                    <div className="relative" data-id="element-1910">
                      <GlobeIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1911" />
                      <input id="pt-country" type="text" placeholder="Your country" className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors" data-id="element-1912" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-id="element-1913">
                  <div data-id="element-1914">
                    <label htmlFor="pt-date" className="block text-sm text-slate-700 font-medium mb-1.5" data-id="element-1915">
                      Travel Date
                    </label>
                    <div className="relative" data-id="element-1916">
                      <CalendarIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1917" />
                      <input id="pt-date" type="date" className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors" data-id="element-1918" />
                    </div>
                  </div>
                  <div data-id="element-1919">
                    <label htmlFor="pt-duration" className="block text-sm text-slate-700 font-medium mb-1.5" data-id="element-1920">
                      Duration
                    </label>
                    <select id="pt-duration" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors appearance-none" data-id="element-1921">
                      <option data-id="element-1922">3–5 days</option>
                      <option data-id="element-1923">5–7 days</option>
                      <option data-id="element-1924">7–10 days</option>
                      <option data-id="element-1925">10–14 days</option>
                      <option data-id="element-1926">14+ days</option>
                    </select>
                  </div>
                  <div data-id="element-1927">
                    <label htmlFor="pt-travelers" className="block text-sm text-slate-700 font-medium mb-1.5" data-id="element-1928">
                      Travelers
                    </label>
                    <div className="relative" data-id="element-1929">
                      <UsersIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1930" />
                      <select id="pt-travelers" className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors appearance-none" data-id="element-1931">
                        <option data-id="element-1932">1 person</option>
                        <option data-id="element-1933">2 people</option>
                        <option data-id="element-1934">3–4 people</option>
                        <option data-id="element-1935">5–8 people</option>
                        <option data-id="element-1936">9+ people</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Trip Type Checkboxes */}
                <div data-id="element-1937">
                  <label className="block text-sm text-slate-700 font-medium mb-3" data-id="element-1938">
                    Trip Type
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-1939">
                    {tripTypeOptions.map(type => <button key={type} type="button" onClick={() => toggleTripType(type)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${tripTypes.includes(type) ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'}`} data-id="element-1940">
                        {tripTypes.includes(type) && <CheckIcon className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" data-id="element-1941" />}
                        {type}
                      </button>)}
                  </div>
                </div>

                {/* Budget */}
                <div data-id="element-1942">
                  <label htmlFor="pt-budget" className="block text-sm text-slate-700 font-medium mb-1.5" data-id="element-1943">
                    Budget Range (per person)
                  </label>
                  <select id="pt-budget" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors appearance-none" data-id="element-1944">
                    <option data-id="element-1945">$1,000 – $2,000</option>
                    <option data-id="element-1946">$2,000 – $4,000</option>
                    <option data-id="element-1947">$4,000 – $6,000</option>
                    <option data-id="element-1948">$6,000 – $10,000</option>
                    <option data-id="element-1949">$10,000+</option>
                  </select>
                </div>

                {/* Special Requirements */}
                <div data-id="element-1950">
                  <label htmlFor="pt-requirements" className="block text-sm text-slate-700 font-medium mb-1.5" data-id="element-1951">
                    Special Requirements
                  </label>
                  <textarea id="pt-requirements" rows={4} placeholder="Dietary needs, accessibility requirements, special occasions, must-see destinations..." className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors resize-none" data-id="element-1952" />
                </div>

                <button type="submit" className="inline-flex items-center gap-2 px-8 py-3.5 bg-teal-600 text-white font-medium rounded-full hover:bg-teal-700 transition-all duration-300 shadow-lg shadow-teal-600/20 text-sm tracking-wide" data-id="element-1953">
                  <SendIcon className="w-4 h-4" data-id="element-1954" />
                  Submit Trip Request
                </button>
              </form>}
          </div>

          {/* Sidebar */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="lg:col-span-2" data-id="element-1955">
            <div className="sticky top-28 space-y-6" data-id="element-1956">
              <div className="bg-[#FAF8F5] rounded-2xl p-6 sm:p-8 border border-[#D4A853]/10" data-id="element-1957">
                <h3 className="font-playfair text-lg font-semibold text-slate-900 mb-5" data-id="element-1958">
                  Why Plan With Us
                </h3>
                <div className="space-y-4" data-id="element-1959">
                  <div className="flex items-start gap-3" data-id="element-1960">
                    <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1961">
                      <CompassIcon className="w-4 h-4 text-teal-600" data-id="element-1962" />
                    </div>
                    <div data-id="element-1963">
                      <p className="text-sm font-medium text-slate-900" data-id="element-1964">
                        100% Customized
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5" data-id="element-1965">
                        Every itinerary is tailored to your preferences
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-1966">
                    <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1967">
                      <StarIcon className="w-4 h-4 text-teal-600" data-id="element-1968" />
                    </div>
                    <div data-id="element-1969">
                      <p className="text-sm font-medium text-slate-900" data-id="element-1970">
                        Expert Local Guides
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5" data-id="element-1971">
                        Certified guides with 15+ years experience
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-1972">
                    <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1973">
                      <ShieldCheckIcon className="w-4 h-4 text-teal-600" data-id="element-1974" />
                    </div>
                    <div data-id="element-1975">
                      <p className="text-sm font-medium text-slate-900" data-id="element-1976">
                        Best Price Guarantee
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5" data-id="element-1977">
                        Competitive pricing with no hidden fees
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-1978">
                    <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1979">
                      <HeartIcon className="w-4 h-4 text-teal-600" data-id="element-1980" />
                    </div>
                    <div data-id="element-1981">
                      <p className="text-sm font-medium text-slate-900" data-id="element-1982">
                        24/7 Support
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5" data-id="element-1983">
                        Round-the-clock assistance during your trip
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#0C1F2C] rounded-2xl p-6 sm:p-8 border border-[#D4A853]/15" data-id="element-1984">
                <h3 className="font-playfair text-lg font-semibold text-white mb-4" data-id="element-1985">
                  Need Help?
                </h3>
                <p className="text-slate-400 text-base mb-5" data-id="element-1986">
                  Prefer to talk to someone? Reach out directly.
                </p>
                <div className="space-y-3" data-id="element-1987">
                  <a href="https://wa.me/9779851032316" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-3 bg-green-600/10 border border-green-500/20 rounded-xl text-green-400 hover:bg-green-600/20 transition-colors" data-id="element-1988">
                    <MessageCircleIcon className="w-5 h-5" data-id="element-1989" />
                    <span className="text-sm font-medium" data-id="element-1990">
                      Chat on WhatsApp
                    </span>
                  </a>
                  <a href="tel:+9779851032316" className="flex items-center gap-3 px-4 py-3 bg-teal-600/10 border border-teal-500/20 rounded-xl text-teal-400 hover:bg-teal-600/20 transition-colors" data-id="element-1991">
                    <PhoneIcon className="w-5 h-5" data-id="element-1992" />
                    <span className="text-sm font-medium" data-id="element-1993">+977 9851032316</span>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
export function PlanTripPage({
  onNavigate
}: PlanTripPageProps) {
  return <div className="w-full" data-id="element-1994">
      <PlanTripHero onNavigate={onNavigate} data-id="element-1995" />
      <TripForm data-id="element-1996" />
    </div>;
}